import React, { useCallback, useEffect, useMemo, useState } from "react";

import { getRoleList, assignRoleToStaff, staffListWithNoRole } from "../../api/Role";

import { LoadingDiv, SweetAlertShow } from "../master/Master";

import usePrivateLink from "../config/usePrivateLink";

import Header from "../master/SideBar";

import Nav from "../master/NavBar";

import { Link, useNavigate } from "react-router-dom";
import { CreateClientRole } from "../../api/Role";



const asArray = (value) => Array.isArray(value) ? value : Array.isArray(value?.data) ? value.data : [];

const modulesFor = (role) => [...new Set(asArray(role.permissions).map(p => p?.management?.name).filter(Boolean))];

const assignedStaff = role => asArray(role.staffs);

const isFull = role => Number(role.id) === 1;

const accessType = role => isFull(role) ? "full" : modulesFor(role).length ? "limited" : "none";

const initialFilters = { search: "", ownership: "all", access: "all", module: "all", assignment: "all", sort: "custom", order: "asc", pageSize: "10" };
const isCustomRole = role => Number(role?.client_id ?? 0) > 0;
const readableError = err => { const d = err?.response?.data; const e = d?.errors; return (e && Object.values(e).flat().join(", ")) || (Array.isArray(d?.message) ? d.message.join(", ") : d?.message) || err?.message || "Something went wrong"; };



export default function Role() {

  const [roles, setRoles] = useState([]);

  const [loading, setLoading] = useState(false);

  const [busy, setBusy] = useState(false);

  const [error, setError] = useState("");

  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const [filters, setFilters] = useState(initialFilters);

  const [page, setPage] = useState(1);

  const [modal, setModal] = useState(null);

  const [modalSearch, setModalSearch] = useState("");

  const [staffList, setStaffList] = useState([]);

  const [selectedRole, setSelectedRole] = useState(null);

  const [selectedStaff, setSelectedStaff] = useState("");

  const [staffSearch, setStaffSearch] = useState("");

  const [staffLoading, setStaffLoading] = useState(false);

  const hasAccess = usePrivateLink();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [creating, setCreating] = useState(false);



  const change = (key, value) => { setFilters(f => ({ ...f, [key]: value })); setPage(1); };



  const loadRoles = useCallback(async () => {

    setLoading(true); setError("");

    try {

      const response = await getRoleList();

      if (response?.data?.status === false) throw new Error(response.data.message || "Unable to load roles");

      setRoles(asArray(response?.data?.data));

    } catch (err) {

      const message = err?.response?.data?.message || err?.message || "Unable to load roles";

      setError(String(message));

      SweetAlertShow("error", "Unable to load roles", String(message));

    } finally { setLoading(false); }

  }, []);



  useEffect(() => {

    try {

      const saved = JSON.parse(localStorage.getItem("user_permissions") || "null");

      setIsSuperAdmin(Number(saved?.id) === 1);

    } catch { setIsSuperAdmin(false); }

    loadRoles();

  }, [loadRoles]);



  const allModules = useMemo(() => [...new Set(roles.flatMap(modulesFor))].sort((a, b) => a.localeCompare(b)), [roles]);

  const filtered = useMemo(() => {

    const search = filters.search.trim().toLowerCase();

    const result = roles.filter(role => {

      const modules = modulesFor(role);

      const staffs = assignedStaff(role);

      if (search && ![role.name, role.id, ...modules, ...staffs.map(s => s.name)].some(v => String(v ?? "").toLowerCase().includes(search))) return false;

      if (filters.ownership === "custom" && !isCustomRole(role)) return false;
      if (filters.ownership === "default" && isCustomRole(role)) return false;
      if (filters.ownership === "custom" && !isCustomRole(role)) return false;
      if (filters.ownership === "default" && isCustomRole(role)) return false;
      if (filters.access !== "all" && accessType(role) !== filters.access) return false;

      if (filters.module !== "all" && !(isFull(role) || modules.includes(filters.module))) return false;

      if (filters.assignment === "assigned" && !staffs.length) return false;

      if (filters.assignment === "unassigned" && staffs.length) return false;

      return true;

    });

    const sortValue = role => filters.sort === "custom" ? (isCustomRole(role) ? 0 : 1) : filters.sort === "staff" ? assignedStaff(role).length : filters.sort === "modules" ? (isFull(role) ? Number.MAX_SAFE_INTEGER : modulesFor(role).length) : filters.sort === "id" ? Number(role.id) : String(role.name || "").toLowerCase();

    result.sort((a, b) => {

      const av = sortValue(a), bv = sortValue(b);

      const cmp = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));

      if (cmp === 0 && filters.sort === "custom") return String(a.name || "").localeCompare(String(b.name || ""));
      if (cmp === 0 && filters.sort === "custom") return String(a.name || "").localeCompare(String(b.name || ""));
      return filters.order === "desc" ? -cmp : cmp;

    });

    return result;

  }, [roles, filters]);



  const pageSize = Number(filters.pageSize);

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));

  const currentPage = Math.min(page, pages);

  const visible = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const stats = useMemo(() => ({ total: roles.length, full: roles.filter(isFull).length, limited: roles.filter(r => accessType(r) === "limited").length, noAccess: roles.filter(r => accessType(r) === "none").length, assigned: roles.filter(r => assignedStaff(r).length).length, staff: roles.reduce((n, r) => n + assignedStaff(r).length, 0) }), [roles]);



  const createRole = async event => {
    event.preventDefault();
    const name = newRoleName.trim();
    if (name.length < 2 || name.length > 255) {
      SweetAlertShow("error", "Invalid role name", "Enter a role name between 2 and 255 characters.");
      return;
    }
    if (creating) return;
    setCreating(true);
    try {
      const response = await CreateClientRole(name);
      const payload = response?.data;
      if (payload?.status !== true || !payload?.data?.id) throw new Error(payload?.message || "Role was not created.");
      const newId = payload.data.id;
      setCreateOpen(false);
      setNewRoleName("");
      SweetAlertShow("success", "Role created", "Now configure permissions for your new role.");
      navigate(`/role/view?id=${encodeURIComponent(newId)}`);
    } catch (err) {
      SweetAlertShow("error", "Unable to create role", String(readableError(err)));
    } finally { setCreating(false); }
  };

  const openDetails = (title, items, type) => { setModal({ title, items, type }); setModalSearch(""); };

  const openAssign = async role => {

    setSelectedRole(role); setSelectedStaff(""); setStaffSearch(""); setStaffList([]); setStaffLoading(true);

    try {

      const response = await staffListWithNoRole({ role_id: role.id });

      if (response?.data?.status === false) throw new Error(response.data.message || "Unable to fetch staff");

      setStaffList(asArray(response?.data?.data));

    } catch (err) {

      SweetAlertShow("error", "Staff loading failed", String(err?.response?.data?.message || err.message));

      setSelectedRole(null);

    } finally { setStaffLoading(false); }

  };

  const assign = async () => {

    if (!selectedStaff || !selectedRole?.id) { SweetAlertShow("info", "Select staff", "Please choose a staff member."); return; }

    setBusy(true);

    try {

      const response = await assignRoleToStaff({ staff_id: selectedStaff, role_id: selectedRole.id });

      if (response?.data?.status === false) throw new Error(response.data.message || "Unable to assign role");

      setSelectedRole(null); setSelectedStaff("");

      SweetAlertShow("success", "Assigned successfully", response?.data?.message || "Role assigned to staff.");

      await loadRoles();

    } catch (err) { SweetAlertShow("error", "Assignment failed", String(err?.response?.data?.message || err.message)); }

    finally { setBusy(false); }

  };

  const staffMatches = staffList.filter(s => [s.name, s.mobile].some(v => String(v || "").toLowerCase().includes(staffSearch.toLowerCase())));

  const modalItems = modal?.items.filter(item => String(modal.type === "staff" ? `${item.name} ${item.mobile || ""}` : item).toLowerCase().includes(modalSearch.toLowerCase())) || [];



  return <>

    {loading && <LoadingDiv />}

    <div className="layout-wrapper layout-content-navbar role-page"><div className="layout-container">

      <Header />

      <div className="layout-page"><Nav /><div className="content-wrapper"><div className="container-fluid flex-grow-1 container-p-y role-content">

        <section className="role-header">

          <div><div className="role-eyebrow">ACCESS CONTROL / ADMINISTRATION</div><h4><i className="ri-shield-user-line" /> Roles Management</h4><p>Manage permissions, role access and staff assignments from one place.</p></div>

          <div className="d-flex flex-wrap gap-2">{hasAccess("/roles/create") && (<button type="button" className="btn btn-primary btn-sm" onClick={() => setCreateOpen(true)}><i className="ri-add-line me-1" /> Create Custom Role</button>)} {hasAccess("/roles/refresh") && (<button type="button" className="btn btn-outline-primary btn-sm" onClick={loadRoles} disabled={loading}><i className="ri-refresh-line me-1" /> Refresh Roles</button>)}</div>

        </section>



        <div className="role-stats">

          {[["Total Roles", stats.total, "ri-shield-line", "blue"], ["Full Access", stats.full, "ri-shield-check-line", "green"], ["Limited Access", stats.limited, "ri-lock-password-line", "purple"], ["No Access", stats.noAccess, "ri-lock-line", "orange"], ["Roles With Staff", stats.assigned, "ri-team-line", "teal"], ["Staff Assignments", stats.staff, "ri-user-settings-line", "slate"]].map(([label, count, icon, tone]) => <div className="role-stat" key={label}><span className={`role-stat-icon ${tone}`}><i className={icon} /></span><div><small>{label}</small><strong>{count}</strong></div></div>)}

        </div>



        <section className="role-panel">

          <div className="role-panel-head"><div><strong><i className="ri-filter-3-line me-1" /> Filter Roles</strong><small>Search by role, permission module or assigned staff</small></div><button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setFilters(initialFilters); setPage(1); }}><i className="ri-restart-line me-1" /> Reset</button></div>

          <div className="role-filters">

            <label className="role-field role-search"><span>Search</span><div className="role-input-icon"><i className="ri-search-line" /><input className="form-control form-control-sm" placeholder="Role name, staff, module..." value={filters.search} onChange={e => change("search", e.target.value)} /></div></label>

            <label className="role-field"><span>Role Type</span><select className="form-select form-select-sm" value={filters.ownership} onChange={e => change("ownership", e.target.value)}><option value="all">All Roles</option><option value="custom">Client Created</option><option value="default">Default Roles</option></select></label>
            <label className="role-field"><span>Access Type</span><select className="form-select form-select-sm" value={filters.access} onChange={e => change("access", e.target.value)}><option value="all">All Access Types</option><option value="full">Full Access</option><option value="limited">Limited Access</option><option value="none">No Modules</option></select></label>

            <label className="role-field"><span>Accessible Module</span><select className="form-select form-select-sm" value={filters.module} onChange={e => change("module", e.target.value)}><option value="all">All Modules</option>{allModules.map(m => <option key={m} value={m}>{m}</option>)}</select></label>

            <label className="role-field"><span>Staff Assignment</span><select className="form-select form-select-sm" value={filters.assignment} onChange={e => change("assignment", e.target.value)}><option value="all">All Roles</option><option value="assigned">Staff Assigned</option><option value="unassigned">No Staff Assigned</option></select></label>

            <label className="role-field"><span>Sort By</span><select className="form-select form-select-sm" value={filters.sort} onChange={e => change("sort", e.target.value)}><option value="custom">Client Created First</option><option value="name">Role Name</option><option value="id">Role ID</option><option value="modules">Modules Count</option><option value="staff">Staff Count</option></select></label>

            <label className="role-field"><span>Order</span><select className="form-select form-select-sm" value={filters.order} onChange={e => change("order", e.target.value)}><option value="asc">Ascending</option><option value="desc">Descending</option></select></label>

          </div>

        </section>



        <section className="role-panel">

          <div className="role-panel-head"><div><strong><i className="ri-list-check-2 me-1" /> Role Access List</strong><small>{filtered.length} of {roles.length} roles match your filters</small></div><div className="role-table-tools"><label>Rows <select className="form-select form-select-sm" value={filters.pageSize} onChange={e => change("pageSize", e.target.value)}>{[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}</select></label></div></div>

          {error && <div className="alert alert-danger m-3">{error}</div>}

          <div className="table-responsive"><table className="table table-hover role-table mb-0"><thead><tr><th>#</th><th>Role Details</th><th>Access Level</th><th>Accessible Modules</th><th>Assigned Staff</th><th>Actions</th></tr></thead><tbody>

            {visible.map((role, index) => {
              const modules = modulesFor(role), staffs = assignedStaff(role), type = accessType(role); return <tr key={role.id || index}>

                <td className="role-index">{(currentPage - 1) * pageSize + index + 1}</td>

                <td><div className="role-identity"><span className={`role-avatar ${type}`}><i className={type === "full" ? "ri-shield-star-line" : "ri-shield-user-line"} /></span><div><strong>{role.name || "Unnamed Role"}</strong><small>Role ID: {role.id} · {isCustomRole(role) ? "Client Created" : "Default Role"}</small></div></div></td>

                <td><span className={`role-status ${type}`}><i className={type === "full" ? "ri-checkbox-circle-line" : type === "limited" ? "ri-shield-keyhole-line" : "ri-lock-line"} />{type === "full" ? "Full Access" : type === "limited" ? "Limited Access" : "No Modules"}</span></td>

                <td>{type === "full" ? <span className="role-subtle">All available modules</span> : modules.length ? <div className="role-chips">{modules.slice(0, 2).map(m => <span className="role-chip" key={m} title={m}>{m}</span>)}{modules.length > 2 && <button className="role-more" type="button" onClick={() => openDetails(`${role.name} — Accessible Modules`, modules, "text")}>+{modules.length - 2} more</button>}<button type="button" className="role-detail-link" onClick={() => openDetails(`${role.name} — Accessible Modules`, modules, "text")}>View all ({modules.length})</button></div> : <span className="role-muted">No modules assigned</span>}</td>

                <td>{staffs.length ? <div className="role-staff"><div className="role-staff-names">{staffs.slice(0, 2).map(s => <Link key={s.id} to={`/teachers/view/${s.id}`} className="role-staff-link">{s.name}</Link>)}{staffs.length > 2 && <button type="button" className="role-more" onClick={() => openDetails(`${role.name} — Assigned Staff`, staffs, "staff")}>+{staffs.length - 2}</button>}</div><button type="button" className="role-detail-link" onClick={() => openDetails(`${role.name} — Assigned Staff`, staffs, "staff")}>View staff ({staffs.length})</button></div> : <span className="role-muted">Not assigned</span>}</td>

                <td><div className="role-actions">{hasAccess("/role/view") && <Link to={`/role/view?id=${role.id}`} className="btn btn-sm btn-outline-primary" title="View role permissions"><i className="ri-eye-line" /> <span>View</span></Link>}{isSuperAdmin && <button type="button" className="btn btn-sm btn-outline-success" title="Assign staff to this role" onClick={() => openAssign(role)}><i className="ri-user-add-line" /> <span>Assign</span></button>}</div></td>

              </tr>;
            })}

            {!visible.length && <tr><td colSpan={6}><div className="role-empty"><i className="ri-search-eye-line" /><strong>No matching roles</strong><span>Try changing the search or filter selections.</span></div></td></tr>}

          </tbody></table></div>

          <div className="role-footer"><small>Showing {filtered.length ? (currentPage - 1) * pageSize + 1 : 0}–{Math.min(currentPage * pageSize, filtered.length)} of {filtered.length} roles</small><div className="role-pagination"><button className="btn btn-sm btn-outline-secondary" disabled={currentPage <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Previous</button><span>Page {currentPage} of {pages}</span><button className="btn btn-sm btn-outline-secondary" disabled={currentPage >= pages} onClick={() => setPage(p => Math.min(pages, p + 1))}>Next</button></div></div>

        </section>

      </div></div></div>

    </div></div>



    {createOpen && <div className="role-overlay" onMouseDown={() => !creating && setCreateOpen(false)}>
      <div className="role-modal" role="dialog" aria-modal="true" aria-label="Create custom role" onMouseDown={e => e.stopPropagation()}>
        <form onSubmit={createRole}>
          <div className="role-modal-head"><div><small>CLIENT ROLE</small><h5><i className="ri-add-circle-line me-2" />Create Custom Role</h5></div><button type="button" className="btn-close" disabled={creating} onClick={() => setCreateOpen(false)} /></div>
          <div className="role-modal-body"><label className="role-field"><span>Role Name *</span><input className="form-control" autoFocus required minLength={2} maxLength={255} placeholder="e.g. Front Office Manager" value={newRoleName} onChange={e => setNewRoleName(e.target.value)} disabled={creating} /></label><p className="text-muted small mt-3 mb-0">The role will be created for your client only. After creation you will be redirected to assign management, category and route permissions.</p></div>
          <div className="role-modal-foot"><button type="button" className="btn btn-outline-secondary" onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</button><button type="submit" className="btn btn-primary" disabled={creating || newRoleName.trim().length < 2}><i className="ri-save-line me-1" />{creating ? "Creating..." : "Create & Set Permissions"}</button></div>
        </form>
      </div>
    </div>}

    {modal && <div className="role-overlay" onMouseDown={() => setModal(null)}><div className="role-modal" onMouseDown={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={modal.title}><div className="role-modal-head"><div><small>ROLE DETAILS</small><h5>{modal.title}</h5></div><button type="button" className="btn-close" aria-label="Close" onClick={() => setModal(null)} /></div><div className="role-modal-body"><div className="role-input-icon mb-3"><i className="ri-search-line" /><input className="form-control" placeholder="Search this list..." value={modalSearch} onChange={e => setModalSearch(e.target.value)} /></div><div className="role-modal-items">{modalItems.map((item, index) => modal.type === "staff" ? <Link key={item.id || index} to={`/teachers/view/${item.id}`} onClick={() => setModal(null)} className="role-modal-item"><i className="ri-user-line" /><div><strong>{item.name}</strong>{item.mobile && <small>{item.mobile}</small>}</div><i className="ri-arrow-right-up-line ms-auto" /></Link> : <div className="role-modal-item" key={`${item}-${index}`}><i className="ri-checkbox-circle-line" /><strong>{item}</strong></div>)}{!modalItems.length && <div className="role-empty">No matching items</div>}</div></div><div className="role-modal-foot"><small>{modalItems.length} item(s)</small><button className="btn btn-outline-secondary btn-sm" onClick={() => setModal(null)}>Close</button></div></div></div>}



    {(selectedRole || staffLoading) && <div className="role-overlay" onMouseDown={() => !busy && !staffLoading && setSelectedRole(null)}><div className="role-modal" onMouseDown={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Assign Staff"><div className="role-modal-head"><div><small>STAFF ACCESS</small><h5><i className="ri-user-add-line me-2" />Assign Staff</h5></div><button type="button" className="btn-close" disabled={busy || staffLoading} onClick={() => setSelectedRole(null)} /></div><div className="role-modal-body"><div className="role-selected"><small>Selected Role</small><strong>{selectedRole?.name || "—"}</strong></div><label className="role-field mt-3"><span>Search available staff</span><div className="role-input-icon"><i className="ri-search-line" /><input className="form-control" value={staffSearch} onChange={e => setStaffSearch(e.target.value)} placeholder="Search by name or mobile..." disabled={staffLoading} /></div></label><div className="role-staff-options">{staffLoading ? <div className="role-empty">Loading available staff...</div> : staffMatches.length ? staffMatches.map(s => <button type="button" key={s.id} className={`role-staff-option ${String(selectedStaff) === String(s.id) ? "selected" : ""}`} onClick={() => setSelectedStaff(String(s.id))}><span className="role-staff-icon"><i className="ri-user-line" /></span><span><strong>{s.name}</strong><small>{s.mobile || `Staff ID: ${s.id}`}</small></span><i className={String(selectedStaff) === String(s.id) ? "ri-checkbox-circle-fill ms-auto" : "ri-checkbox-blank-circle-line ms-auto"} /></button>) : <div className="role-empty">No available staff match your search.</div>}</div><small className="text-muted d-block mt-2">{staffMatches.length} of {staffList.length} available staff</small></div><div className="role-modal-foot"><button className="btn btn-outline-secondary" disabled={busy} onClick={() => setSelectedRole(null)}>Cancel</button><button className="btn btn-primary" disabled={busy || staffLoading || !selectedStaff} onClick={assign}><i className="ri-user-add-line me-1" />{busy ? "Assigning..." : "Assign Staff"}</button></div></div></div>}



    <style>{`

      .role-page{--role-border:var(--bs-border-color,#e4e8ef);--role-text:var(--bs-body-color,#414a59)}

      .role-content{max-width:1750px;margin:0 auto}

      .role-header,.role-panel,.role-stat{background:var(--bs-body-bg,#fff);border:1px solid var(--role-border);border-radius:12px;box-shadow:0 4px 16px rgba(30,40,65,.035)}

      .role-header{display:flex;justify-content:space-between;align-items:center;gap:14px;padding:19px 21px;margin-bottom:13px}

      .role-eyebrow{font-size:10px;font-weight:800;letter-spacing:.08em;color:#696cff}

      .role-header h4{font-size:21px;font-weight:800;margin:4px 0 3px}.role-header h4 i{color:#696cff;margin-right:7px}.role-header p{margin:0;font-size:12px;color:#8d95a3}

      .role-stats{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));gap:10px;margin-bottom:13px}

      .role-stat{padding:13px;display:flex;align-items:center;gap:10px;min-width:0}.role-stat-icon{width:37px;height:37px;border-radius:10px;display:grid;place-items:center;flex-shrink:0;font-size:19px}.role-stat-icon.blue{background:#eaf0ff;color:#5076e8}.role-stat-icon.green{background:#e8f8ef;color:#2c9a5c}.role-stat-icon.purple{background:#eeedff;color:#696cff}.role-stat-icon.orange{background:#fff1e3;color:#c67c2a}.role-stat-icon.teal{background:#e4f8f7;color:#168e8a}.role-stat-icon.slate{background:#edf1f6;color:#66758c}

      .role-stat small{display:block;font-size:10px;color:#8993a2;line-height:1.4}.role-stat strong{display:block;font-size:21px;font-weight:800;line-height:1.3}

      .role-panel{overflow:hidden;margin-bottom:13px}.role-panel-head{padding:13px 16px;border-bottom:1px solid var(--role-border);display:flex;justify-content:space-between;align-items:center;gap:10px}.role-panel-head strong{font-size:14px}.role-panel-head small{display:block;color:#9098a6;font-size:11px;margin-top:3px}

      .role-filters{padding:14px 16px;display:grid;grid-template-columns:2fr repeat(6,minmax(0,1fr));gap:10px}.role-field{min-width:0}.role-field>span{display:block;font-size:10px;font-weight:750;color:#7d8797;margin-bottom:5px}.role-field .form-control,.role-field .form-select{min-height:37px;font-size:11px;border-radius:8px}

      .role-input-icon{position:relative}.role-input-icon>i{position:absolute;left:11px;top:50%;transform:translateY(-50%);color:#9299a8;pointer-events:none}.role-input-icon input{padding-left:34px}

      .role-table-tools label{display:flex;align-items:center;gap:8px;font-size:11px;color:#9299a6}.role-table-tools select{width:75px}

      .role-table{min-width:950px}.role-table thead th{font-size:10px;text-transform:uppercase;letter-spacing:.035em;color:#697586;background:var(--bs-tertiary-bg,#f6f7f9);padding:12px 14px;white-space:nowrap}.role-table tbody td{padding:13px 14px;font-size:11px;vertical-align:middle;border-bottom:1px solid var(--role-border)}.role-index{color:#8993a2;font-weight:700}

      .role-identity{display:flex;align-items:center;gap:10px;min-width:160px}.role-identity strong{display:block;font-size:12px}.role-identity small{display:block;color:#9199a6;font-size:10px;margin-top:2px}.role-avatar{width:37px;height:37px;display:grid;place-items:center;border-radius:10px;background:#f0efff;color:#696cff;font-size:19px;flex-shrink:0}.role-avatar.full{background:#e6f7ed;color:#278f57}.role-avatar.none{background:#fff0e6;color:#d17b35}

      .role-status{display:inline-flex;align-items:center;gap:5px;border-radius:7px;padding:6px 9px;font-size:10px;font-weight:800;white-space:nowrap}.role-status.full{background:#e5f8ed;color:#20834b}.role-status.limited{background:#efeeff;color:#6159d7}.role-status.none{background:#fff0e5;color:#ba6a26}

      .role-chips,.role-staff-names{display:flex;flex-wrap:wrap;align-items:center;gap:5px}.role-chip{font-size:10px;color:#5e65c8;background:#f0efff;border-radius:5px;padding:5px 7px;max-width:150px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.role-more{border:1px solid #dce0fc;background:#fafaff;color:#6366cc;border-radius:6px;font-size:10px;padding:4px 6px}.role-detail-link{display:inline-block;border:0;background:transparent;color:#696cff;padding:2px;font-size:10px;text-decoration:underline;text-underline-offset:2px}.role-staff-link{color:#167f96;background:#e8f6fa;text-decoration:none;padding:5px 7px;border-radius:6px;font-size:10px;max-width:140px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.role-subtle,.role-muted{font-size:11px;color:#9099a7}.role-actions{display:flex;gap:6px;white-space:nowrap}.role-actions .btn{font-size:10px;padding:6px 9px}

      .role-footer{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:12px 16px}.role-footer small{font-size:11px;color:#8993a2}.role-pagination{display:flex;align-items:center;gap:9px}.role-pagination span{font-size:11px;color:#657084}.role-empty{padding:35px 15px;text-align:center;color:#9299a6;display:flex;flex-direction:column;align-items:center;gap:6px}.role-empty i{font-size:28px;color:#a8a7e5}.role-empty strong{font-size:13px;color:#6d7482}.role-empty span{font-size:11px}

      .role-overlay{position:fixed;inset:0;z-index:1200;background:rgba(22,30,50,.62);display:grid;place-items:center;padding:12px}.role-modal{background:var(--bs-body-bg,#fff);color:var(--bs-body-color,#454b55);width:min(570px,100%);max-height:94vh;display:flex;flex-direction:column;border-radius:14px;box-shadow:0 25px 75px #0003}.role-modal-head{display:flex;align-items:flex-start;justify-content:space-between;gap:10px;padding:18px;border-bottom:1px solid var(--role-border)}.role-modal-head small{color:#696cff;font-size:10px;font-weight:800}.role-modal-head h5{font-size:17px;font-weight:800;margin:5px 0 0}.role-modal-body{padding:17px;overflow-y:auto}.role-modal-foot{display:flex;justify-content:space-between;align-items:center;gap:8px;padding:13px 17px;border-top:1px solid var(--role-border)}.role-modal-foot small{font-size:11px;color:#9299a6}.role-modal-items{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}.role-modal-item{display:flex;align-items:center;gap:9px;border:1px solid var(--role-border);border-radius:9px;padding:11px;text-decoration:none;color:inherit;font-size:11px;min-width:0}.role-modal-item>i:first-child{font-size:18px;color:#696cff}.role-modal-item strong{font-size:11px;overflow-wrap:anywhere}.role-modal-item small{display:block;color:#9299a6}.role-selected{background:#f3f2ff;padding:12px;border-radius:9px;color:#5758bd}.role-selected small{display:block;font-size:10px}.role-selected strong{display:block;font-size:14px}.role-staff-options{margin-top:10px;max-height:260px;overflow:auto;border:1px solid var(--role-border);border-radius:9px}.role-staff-option{display:flex;align-items:center;gap:10px;width:100%;text-align:left;border:0;border-bottom:1px solid var(--role-border);background:transparent;color:inherit;padding:10px 12px}.role-staff-option:last-child{border-bottom:0}.role-staff-option.selected{background:#f2f1ff}.role-staff-option>span:nth-child(2){display:flex;flex-direction:column}.role-staff-option strong{font-size:12px}.role-staff-option small{font-size:10px;color:#9299a6}.role-staff-icon{background:#eeedff;color:#696cff;width:33px;height:33px;display:grid;place-items:center;border-radius:50%;flex-shrink:0}

      @media(max-width:1400px){.role-stats{grid-template-columns:repeat(3,minmax(0,1fr))}.role-filters{grid-template-columns:repeat(3,minmax(0,1fr))}.role-search{grid-column:span 1}}

      @media(max-width:767px){.role-content{padding-left:10px!important;padding-right:10px!important}.role-header{flex-direction:column;align-items:flex-start}.role-stats{grid-template-columns:repeat(2,minmax(0,1fr))}.role-filters{grid-template-columns:repeat(2,minmax(0,1fr))}.role-search{grid-column:span 2}.role-modal-items{grid-template-columns:1fr}.role-header h4{font-size:18px}}

      @media(max-width:420px){.role-stats{gap:7px}.role-stat{padding:9px;gap:7px}.role-stat-icon{width:31px;height:31px;font-size:16px}.role-stat strong{font-size:17px}.role-filters{grid-template-columns:1fr}.role-search{grid-column:span 1}.role-footer{flex-direction:column;align-items:flex-start}}



      /* White-card design shared with the attendance and MasterV2 pages */

      .role-page{--role-border:#e3e7ed;--role-text:#454b55;background:#f6f7fb!important;color:#454b55;min-height:100vh}

      .role-page .layout-container,.role-page .layout-page,.role-page .content-wrapper{min-width:0}

      .role-page .role-content{max-width:1750px;margin:0 auto;padding-top:18px!important}

      .role-page .role-header,.role-page .role-panel,.role-page .role-stat{background:#fff!important;border:1px solid #e3e7ed!important;border-radius:12px!important;box-shadow:0 4px 18px rgba(31,45,61,.035)!important}

      .role-page .role-header{padding:15px 18px;margin-bottom:11px}

      .role-page .role-eyebrow{color:#696cff;font-size:10px}

      .role-page .role-header h4{font-size:20px;color:#25293c;font-weight:850;margin:3px 0}

      .role-page .role-header p{font-size:11px;color:#9097a2}

      .role-page .role-stats{gap:9px;margin-bottom:11px}

      .role-page .role-stat{padding:11px 12px;gap:9px;min-height:69px}

      .role-page .role-stat-icon{height:34px;width:34px;font-size:17px;border-radius:9px}

      .role-page .role-stat small{font-size:10px;color:#9299a4}

      .role-page .role-stat strong{font-size:19px;color:#454b55}

      .role-page .role-panel{margin-bottom:11px;overflow:hidden}

      .role-page .role-panel-head{padding:12px 14px;border-bottom:1px solid #e8ebef;background:#fff}

      .role-page .role-panel-head strong{font-size:14px;color:#444b55}

      .role-page .role-panel-head small{font-size:10px;color:#9299a4}

      .role-page .role-filters{padding:13px 14px;gap:9px;background:#fff}

      .role-page .role-field>span{font-size:10px;color:#737b86}

      .role-page .role-field .form-control,.role-page .role-field .form-select{background:#fff;color:#4e5560;border:1px solid #dce1e7;border-radius:8px;min-height:39px;font-size:11px;box-shadow:none}

      .role-page .role-field .form-control:focus,.role-page .role-field .form-select:focus{border-color:#696cff;box-shadow:0 0 0 2px rgba(105,108,255,.10)}

      .role-page .role-table{background:#fff;min-width:950px}

      .role-page .role-table thead th{background:#f6f7f9!important;color:#616a76!important;border-bottom:1px solid #dfe3e8!important;font-size:10px;padding:11px 10px}

      .role-page .role-table tbody td{background:#fff;color:#4d545f;font-size:11px;padding:11px 10px;border-bottom:1px solid #eceff2}

      .role-page .role-table tbody tr:hover td{background:#fafbff}

      .role-page .role-avatar{width:35px;height:35px;border-radius:9px}

      .role-page .role-chip{background:#f0efff;color:#5e65c8}

      .role-page .role-footer{background:#fff;border-top:1px solid #e8ebef;padding:11px 14px}

      .role-page .role-modal{background:#fff;color:#454b55;border:1px solid #e3e7ed}

      .role-page .role-modal-head,.role-page .role-modal-foot{background:#fff}

      .role-page .role-modal-body{background:#fff}

      .role-page .role-modal-item{background:#fff}

      .role-page .role-modal-item:hover{background:#f8f8ff}

      .role-page .role-selected{background:#f3f2ff}

      .role-page .role-staff-option{background:#fff}

      .role-page .role-staff-option.selected{background:#f2f1ff}

      @media(max-width:767px){.role-page .role-content{padding:10px!important}.role-page .role-header{padding:13px}.role-page .role-stat{padding:10px}.role-page .role-panel-head{padding:11px 12px}}

    `}</style>

  </>;

}
